import { Button, TextButton } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import { ImageStack } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { isDefined, localize } from 'common/utils';
import { deletePublishRequest, deleteSharedMenuDraft } from 'modules/shared-menu/api';
import { ListingManagerErrorPage } from 'modules/shared-menu/components/ListingManagerErrorPage';
import { MenuEditorRoot } from 'modules/shared-menu/components/MenuEditorRoot';
import { OnboardingPopover } from 'modules/shared-menu/components/Onboarding/OnboardingPopover';
import SharedMenuStateTag from 'modules/shared-menu/components/SharedMenuStateTag';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { useListingManagerImageGalleryFeatureFlag } from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/hooks/useListingManagerImageGalleryFeatureFlag';
import { getErrorResponseText } from 'modules/shared-menu/utils';

import AddCategoryModal from './components/AddCategoryModal';
import { EditorStickyFooter } from './components/EditorStickyFooter';
import { ImmediatePublishModal } from './components/ImmediatePublishModal';
import { MenuBootstrapModal } from './components/MenuBootstrapModal';
import { SchedulePublishModal } from './components/SchedulePublishModal';
import { SharedMenuActions } from './components/SharedMenuActions';
import MenuEditorDnDContext from './components/dnd/MenuEditorDnDContext';
import { MenuMainElementRefContext } from './context/MenuMainElementRefContext';
import { WoltSpecialsContextProvider } from './context/WoltSpecialsContext';
import { useSelectionProducts } from './hooks/useSelectionProducts';
import { HeaderContainer, Root } from '../../components/CommonStyles';
import { useSelectionData } from '../../hooks/useSelectionData';
import { SharedMenu } from '../../types';
import { CentralisedAddButton } from '../SharedMenuPage/components/CentralisedAddButton';

type Props = {
  sharedMenu: SharedMenu;
  venuesWithLinks: VenueWithSharedMenuLinkInfo[];
};

const StyledTextButton = styled(TextButton)`
  font-weight: bold;
  margin-block-end: 8px;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const SharedMenuHeader = styled(T.Heading4)`
  display: flex;
  flex-direction: column;
`;

const SharedMenuNameAndStatus = styled.div`
  display: flex;
  align-items: center;
`;

const SharedMenuNameContainer = styled.div`
  margin-inline-end: 8px;
`;

const SharedMenuActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const SharedMenuEditorPage = ({ sharedMenu, venuesWithLinks }: Props) => {
  const history = useHistory();
  const rootRef = useRef<HTMLDivElement>(null);
  const mainElementRef = useRef<HTMLDivElement>(null);
  const { rootPage, menuEditorImageGalleryBrowseImagesPage } = useListingManagerRoutes();
  const { isListingManagerImageGalleryEnabled } = useListingManagerImageGalleryFeatureFlag();
  const menuPermissions = useOfferingMenuPermissionsContext();

  const scope = useScopeContext();
  const { updateSharedMenuStatus } = useSharedMenuData({ scope });
  const {
    loadingInitialSelectionData,
    selection,
    refetchSelectionData,
    loadingSelectionData,
    selectionDataError,
    offers,
  } = useSelectionData();
  const { loadingSelectionProducts } = useSelectionProducts(offers?.map(offer => offer.product_id).filter(isDefined));

  const [isImmediatePublishModalOpen, setIsImmediatePublishModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isBootstrapModalOpen, setIsBootstrapModalOpen] = useState(false);
  const [isDiscardChangesModalOpen, setIsDiscardChangesModalOpen] = useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);

  const handleStartMenuBootstrap = useCallback(() => {
    setIsBootstrapModalOpen(true);
  }, []);

  const { mutate: executeDeleteSharedMenuDraft, isLoading: isLoadingDeletingDrafts } = useMutation(
    async () => {
      // If publishing is scheduled, a publish task will exist in the database
      // Delete the publishing task before deleting the draft itself
      const hasScheduledPublishTask = !!sharedMenu.draft?.publish_at;
      if (hasScheduledPublishTask) {
        await deletePublishRequest(sharedMenu.id);
      }
      return deleteSharedMenuDraft(sharedMenu.id);
    },
    {
      onError: (error: any) => {
        toast.error(getErrorResponseText(error), { duration: Infinity });
        setIsDiscardChangesModalOpen(false);
      },
      onSuccess: async () => {
        await refetchSelectionData();
        updateSharedMenuStatus(sharedMenu.id, undefined);
        setIsDiscardChangesModalOpen(false);
        toast.success(
          <FormattedMessage
            id="shared-menu.delete-draft.toast.success"
            values={{
              sharedMenuName: localize(sharedMenu.name, sharedMenu.primary_language),
              strong: chunks => <strong>{chunks}</strong>,
            }}
          />,
        );
      },
    },
  );

  if (loadingInitialSelectionData || loadingSelectionProducts) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="blue" />
      </SpinnerContainer>
    );
  }

  if (selectionDataError) {
    return <ListingManagerErrorPage error={selectionDataError} />;
  }

  return (
    <WoltSpecialsContextProvider>
      <MenuMainElementRefContext.Provider value={mainElementRef}>
        <Root ref={rootRef}>
          <MenuEditorDnDContext>
            <HeaderContainer>
              <SharedMenuHeader>
                <StyledTextButton
                  variant="neutral"
                  size="small"
                  icon={<ArrowLeft />}
                  onClick={() => history.push(rootPage())}
                >
                  <FormattedMessage id="shared-menu.editor.back-button" />
                </StyledTextButton>
                <SharedMenuNameAndStatus>
                  <SharedMenuNameContainer>
                    {localize(sharedMenu.name, sharedMenu.primary_language)}
                  </SharedMenuNameContainer>
                  {selection && <SharedMenuStateTag sharedMenu={selection} />}
                </SharedMenuNameAndStatus>
              </SharedMenuHeader>
              <SharedMenuActionsContainer>
                {sharedMenu && <SharedMenuActions sharedMenu={sharedMenu} />}
                {isListingManagerImageGalleryEnabled && (
                  <Button
                    variant="secondary"
                    icon={<ImageStack />}
                    size="small"
                    onClick={() => history.push(menuEditorImageGalleryBrowseImagesPage())}
                  >
                    <FormattedMessage id="shared-menu.editor.image-gallery.button-label" />
                  </Button>
                )}
                {menuPermissions.contentEdit && (
                  <OnboardingPopover placement="bottom-end" step="ONBOARDING_STEP_4_CREATE">
                    <CentralisedAddButton />
                  </OnboardingPopover>
                )}
              </SharedMenuActionsContainer>
            </HeaderContainer>

            <MenuEditorRoot onStartMenuBootstrap={handleStartMenuBootstrap} />

            <ImmediatePublishModal
              isOpen={isImmediatePublishModalOpen}
              onClose={() => setIsImmediatePublishModalOpen(false)}
              selectionId={sharedMenu.id}
            />

            {selection && (
              <SchedulePublishModal
                isOpen={isScheduleModalOpen}
                onClose={() => setIsScheduleModalOpen(false)}
                selection={selection}
              />
            )}

            {isBootstrapModalOpen && (
              <MenuBootstrapModal
                isOpen
                onClose={() => setIsBootstrapModalOpen(false)}
                venuesWithLinks={venuesWithLinks}
              />
            )}

            <ConfirmModal
              header={<FormattedMessage id="shared-menu.delete-draft.modal.title" />}
              description={
                <T.Body3>
                  <FormattedMessage id="shared-menu.delete-draft.modal.description" />
                </T.Body3>
              }
              onCancel={() => setIsDiscardChangesModalOpen(false)}
              onConfirm={() => executeDeleteSharedMenuDraft()}
              confirmVariant="secondaryNegative"
              cancelText={<FormattedMessage id="state.cancel" />}
              confirmText={<FormattedMessage id="shared-menu.delete-draft.modal.confirm" />}
              open={isDiscardChangesModalOpen}
              loading={isLoadingDeletingDrafts || loadingSelectionData}
            />

            <AddCategoryModal isOpen={isAddCategoryModalOpen} onClose={() => setIsAddCategoryModalOpen(false)} />
          </MenuEditorDnDContext>
          <EditorStickyFooter
            onDiscardClick={() => setIsDiscardChangesModalOpen(true)}
            onScheduleClick={() => setIsScheduleModalOpen(true)}
            onPublishClick={() => setIsImmediatePublishModalOpen(true)}
          />
        </Root>
      </MenuMainElementRefContext.Provider>
    </WoltSpecialsContextProvider>
  );
};
